import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { Truck, RefreshCw, CheckCircle2, Clock, Banknote, CreditCard, Smartphone, Receipt } from 'lucide-react';
// ──────────────────────────────────────────────────────────────────
// DeliveryMsExpressSection — control OPERATIVO de pago al repartidor.
//
// Esta sección es SOLO para que caja vea los pedidos con delivery_fee > 0
// y marque si el delivery ya fue pagado al mensajero. Es totalmente
// INDEPENDIENTE del resto del sistema:
//   • NO afecta ventas, totales, subtotal, food_subtotal, grand_total.
//   • NO afecta cierre de caja, facturación ni KDS.
//   • El checkbox SÓLO actualiza orders.delivery_paid_to_driver y
//     orders.delivery_paid_at — nada más.
//
// Reglas de datos:
//   • delivery_fee > 0
//   • status <> 'cancelled' (y variantes anuladas/reembolsadas)
//   • restaurant_id actual
//   • respeta el rango de fechas que recibe por props (filtro de métricas)
// ──────────────────────────────────────────────────────────────────
interface DeliveryMsExpressSectionProps {
  restaurantId: number | string;
  /** YYYY-MM-DD — inicio del rango (mismo filtro que Métricas). */
  startDate: string;
  /** YYYY-MM-DD — fin del rango (mismo filtro que Métricas). */
  endDate: string;
}
interface DeliveryOrderRow {
  id: string;
  order_number: string | null;
  customer_name: string | null;
  created_at: string;
  payment_method: string | null;
  delivery_zone: string | null;
  delivery_subzone: string | null;
  delivery_fee: number;
  delivery_paid_to_driver: boolean;
  delivery_paid_at: string | null;
  delivery_driver_name: string | null;
}
const EXCLUDED_STATUSES = new Set(['cancelled', 'canceled', 'cancelado', 'refunded', 'reembolsado', 'voided', 'rejected']);
const methodMeta = (m: string | null) => {
  switch ((m || '').toLowerCase().trim()) {
    case 'cash':
    case 'efectivo':
      return {
        label: 'Efectivo',
        Icon: Banknote,
        chip: 'bg-emerald-50 text-emerald-700 border-emerald-200'
      };
    case 'sinpe':
      return {
        label: 'SINPE',
        Icon: Smartphone,
        chip: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    case 'card':
    case 'tarjeta':
      return {
        label: 'Tarjeta',
        Icon: CreditCard,
        chip: 'bg-violet-50 text-violet-700 border-violet-200'
      };
    case 'transfer':
    case 'transferencia':
      return {
        label: 'Transferencia',
        Icon: Smartphone,
        chip: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    default:
      return {
        label: m || 'Sin método',
        Icon: Receipt,
        chip: 'bg-gray-100 text-gray-700 border-gray-300'
      };
  }
};
const fmtTimeCR = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString('es-CR', {
      timeZone: 'America/Costa_Rica',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
const fmtFullCR = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('es-CR', {
      timeZone: 'America/Costa_Rica',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
export function DeliveryMsExpressSection({
  restaurantId,
  startDate,
  endDate
}: DeliveryMsExpressSectionProps) {
  const [rows, setRows] = useState<DeliveryOrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** ids actualmente guardando para deshabilitar su checkbox. */
  const [saving, setSaving] = useState<Set<string>>(new Set());
  // Emilius Pizza (id 27) — esta sección se llama "Emilius Express" y
  // pide el nombre del repartidor (vía modal) al marcar un pedido como
  // pagado. Para cualquier otro restaurante el comportamiento queda
  // EXACTAMENTE igual que antes (MS Express, checkbox directo).
  const isEmilius = Number(restaurantId) === 27;
  const brandName = isEmilius ? 'Emilius Express' : 'MS Express';
  // Fila para la que el modal de "nombre del repartidor" está abierto.
  const [driverModalRow, setDriverModalRow] = useState<DeliveryOrderRow | null>(null);
  const [driverNameInput, setDriverNameInput] = useState('');
  const load = useCallback(async () => {
    if (!restaurantId || !startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      // Costa Rica = UTC-6 (sin horario de verano). Anclamos el rango al
      // calendario CR para que coincida con el filtro de Métricas.
      const fromIso = `${startDate}T00:00:00.000-06:00`;
      const toIso = `${endDate}T23:59:59.999-06:00`;
      const {
        data,
        error: qErr
      } = await supabase.from('orders').select('id, order_number, customer_name, created_at, payment_method, delivery_zone, delivery_subzone, delivery_fee, delivery_paid_to_driver, delivery_paid_at, delivery_driver_name, status').eq('restaurant_id', Number(restaurantId)).gt('delivery_fee', 0).gte('created_at', fromIso).lte('created_at', toIso).order('created_at', {
        ascending: false
      });
      if (qErr) throw qErr;
      const filtered = (data || []).filter((o: any) => !EXCLUDED_STATUSES.has(String(o.status || '').toLowerCase().trim()));
      setRows(filtered.map((o: any) => ({
        id: String(o.id),
        order_number: o.order_number ?? null,
        customer_name: o.customer_name ?? null,
        created_at: String(o.created_at),
        payment_method: o.payment_method ?? null,
        delivery_zone: o.delivery_zone ?? null,
        delivery_subzone: o.delivery_subzone ?? null,
        delivery_fee: Math.max(0, Number(o.delivery_fee) || 0),
        delivery_paid_to_driver: o.delivery_paid_to_driver === true,
        delivery_paid_at: o.delivery_paid_at ?? null,
        delivery_driver_name: o.delivery_driver_name ?? null
      })));
    } catch (err: any) {
      console.error('[DeliveryMsExpress] load error:', err);
      setError(err?.message || 'Error cargando pedidos de delivery.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, startDate, endDate]);
  useEffect(() => {
    load();
  }, [load]);
  // Checkbox: SÓLO actualiza delivery_paid_to_driver + delivery_paid_at.
  // Optimista en UI; revierte si la actualización falla.
  const togglePaid = useCallback(async (row: DeliveryOrderRow, nextPaid: boolean, driverName?: string | null) => {
    const nextPaidAt = nextPaid ? new Date().toISOString() : null;
    // Solo Emilius maneja el nombre del repartidor. Al marcar pagado se
    // guarda el nombre; al desmarcar se limpia. Para los demás
    // restaurantes `nextDriverName` queda undefined y NO se toca la
    // columna (update parcial), preservando su comportamiento actual.
    const nextDriverName = isEmilius ? nextPaid ? (driverName || '').trim() || null : null : undefined;
    // Optimistic update
    setRows(prev => prev.map(r => r.id === row.id ? {
      ...r,
      delivery_paid_to_driver: nextPaid,
      delivery_paid_at: nextPaidAt,
      ...(nextDriverName !== undefined ? {
        delivery_driver_name: nextDriverName
      } : {})
    } : r));
    setSaving(prev => new Set(prev).add(row.id));
    try {
      const updatePayload: Record<string, any> = {
        delivery_paid_to_driver: nextPaid,
        delivery_paid_at: nextPaidAt
      };
      if (nextDriverName !== undefined) {
        updatePayload.delivery_driver_name = nextDriverName;
      }
      const {
        error: updErr
      } = await supabase.from('orders').update(updatePayload).eq('id', row.id).eq('restaurant_id', Number(restaurantId));
      if (updErr) throw updErr;
    } catch (err: any) {
      console.error('[DeliveryMsExpress] toggle error:', err);
      // Revert on failure
      setRows(prev => prev.map(r => r.id === row.id ? {
        ...r,
        delivery_paid_to_driver: row.delivery_paid_to_driver,
        delivery_paid_at: row.delivery_paid_at,
        delivery_driver_name: row.delivery_driver_name
      } : r));
      setError(err?.message || 'No se pudo actualizar el estado de pago.');
    } finally {
      setSaving(prev => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  }, [restaurantId, isEmilius]);
  // Emilius: al hacer click en el checkbox abrimos el modal para capturar
  // el nombre del repartidor antes de marcar como pagado. Al desmarcar se
  // limpia directo (sin modal). Otros restaurantes usan togglePaid directo.
  const handleCheckboxChange = useCallback((row: DeliveryOrderRow, nextPaid: boolean) => {
    if (isEmilius && nextPaid) {
      setDriverNameInput(row.delivery_driver_name || '');
      setDriverModalRow(row);
      return;
    }
    togglePaid(row, nextPaid);
  }, [isEmilius, togglePaid]);
  const confirmDriverModal = useCallback(() => {
    if (!driverModalRow) return;
    const name = driverNameInput.trim();
    if (!name) return;
    togglePaid(driverModalRow, true, name);
    setDriverModalRow(null);
    setDriverNameInput('');
  }, [driverModalRow, driverNameInput, togglePaid]);
  const totals = useMemo(() => {
    let cobrado = 0;
    let pagado = 0;
    for (const r of rows) {
      cobrado += r.delivery_fee;
      if (r.delivery_paid_to_driver) pagado += r.delivery_fee;
    }
    return {
      cobrado,
      pagado,
      pendiente: Math.max(0, cobrado - pagado)
    };
  }, [rows]);
  return <Card className="border-cyan-200" data-id="element-681" data-mp="3him28g4wwnne">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-682" data-mp="3hhkdj8uz42n7">
        <div className="flex items-center gap-3" data-id="element-683" data-mp="3ck2fwemep9ui">
          <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center" data-id="element-684" data-mp="37skfbroapba2">
            <Truck className="w-5 h-5 text-cyan-600" data-id="element-685" data-mp="37qpu0c943h5g" />
          </div>
          <div data-id="element-686" data-mp="33r76r9ttitzg">
            <CardTitle className="text-lg" data-id="element-687" data-mp="33ouoevk5ehlv">🚚 Delivery {brandName}</CardTitle>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-688" data-mp="2ytfh6mgnbjyf">
              Control de pago al repartidor. Marcá cada pedido cuyo delivery ya
              fue pagado al mensajero. No afecta ventas ni cierre de caja.
            </p>
          </div>
        </div>
        <button onClick={() => load()} disabled={loading} className="h-9 w-9 rounded-lg flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors disabled:opacity-50 flex-shrink-0" title="Refrescar" aria-label="Refrescar" data-id="element-689" data-mp="2eq9j1z7cygdu">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} data-id="element-690" data-mp="2tlz8y8fnzdnu" />
        </button>
      </CardHeader>

      <CardContent className="space-y-5" data-id="element-691" data-mp="1w0l4vhrq7el6">
        {/* Resumen */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-692" data-mp="20xtmnh8ypglw">
          <div className="rounded-xl border border-cyan-100 bg-cyan-50/50 p-3" data-id="element-693" data-mp="20w0d9u6ysia7">
            <p className="text-[10px] font-bold text-cyan-700 uppercase tracking-wider" data-id="element-694" data-mp="25w4x9u72zv56">
              Total delivery cobrado
            </p>
            <p className="text-2xl font-black text-gray-900 tabular-nums mt-1" data-id="element-695" data-mp="1d90pjhglt2pp">
              {formatCurrency(totals.cobrado)}
            </p>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3" data-id="element-696" data-mp="1i60vlsdge5gt">
            <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider" data-id="element-697" data-mp="1a5l2zattvssq">
              Pagado al repartidor
            </p>
            <p className="text-2xl font-black text-emerald-700 tabular-nums mt-1" data-id="element-698" data-mp="1jwz4fo5cb2oi">
              {formatCurrency(totals.pagado)}
            </p>
          </div>
          <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3" data-id="element-699" data-mp="1eyny3ne97ab1">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider" data-id="element-700" data-mp="r933264tsmle">
              Pendiente por pagar
            </p>
            <p className="text-2xl font-black text-amber-700 tabular-nums mt-1" data-id="element-701" data-mp="mbl3h6cp7jq2">
              {formatCurrency(totals.pendiente)}
            </p>
          </div>
        </div>

        {/* Tabla / estados */}
        {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-702" data-mp="2cl4rwins35s1">
            {error}
          </div> : loading ? <div className="space-y-2" data-id="element-703" data-mp="27onu0wqs1d1r">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 w-full rounded-lg" data-id="element-704" data-mp="2f4hmripynuna" />)}
          </div> : rows.length === 0 ? <div className="text-center py-10 px-4 text-gray-500 text-sm border border-dashed border-gray-200 rounded-xl" data-id="element-705" data-mp="2k2sxrwrzsb73">
            <p className="font-medium text-gray-700" data-id="element-706" data-mp="2a6pycyoiyqnu">
              No hay pedidos con delivery en este rango.
            </p>
            <p className="text-xs text-gray-500 mt-2 max-w-md mx-auto leading-relaxed" data-id="element-707" data-mp="2xxljjcyh6nm4">
              Aparecen aquí los pedidos con costo de delivery mayor a ₡0 que no
              estén cancelados, dentro del rango de fechas seleccionado.
            </p>
          </div> : <div className="overflow-x-auto -mx-6" data-id="element-708" data-mp="2t526qi7m3wid">
            <table className="w-full text-sm text-left min-w-[860px]" data-id="element-709" data-mp="2t3r08ams8ank">
              <thead className="text-[11px] text-gray-500 uppercase bg-gray-50" data-id="element-710" data-mp="3gumst9z2b9vv">
                <tr data-id="element-711" data-mp="3ln6eyvbtfdca">
                  <th className="px-6 py-2.5 rounded-l-lg" data-id="element-712" data-mp="3lnpkfcka499f">Pedido</th>
                  <th className="px-3 py-2.5" data-id="element-713" data-mp="1xy36ndd9pb4h">Cliente</th>
                  <th className="px-3 py-2.5" data-id="element-714" data-mp="1xvgq4o8efla4">Hora</th>
                  <th className="px-3 py-2.5" data-id="element-715" data-mp="1sxypdl4jccd7">Método</th>
                  <th className="px-3 py-2.5" data-id="element-716" data-mp="1nzncf1iy1lqr">Zona</th>
                  <th className="px-3 py-2.5" data-id="element-717" data-mp="1o05j6s38ficx">Localidad</th>
                  <th className="px-3 py-2.5 text-right" data-id="element-718" data-mp="1jyjywlodc1sp">Costo delivery</th>
                  <th className="px-3 py-2.5" data-id="element-719" data-mp="2gob5hj9xpfev">Estado pago</th>
                  <th className="px-3 py-2.5 text-center rounded-r-lg" data-id="element-720" data-mp="2gnt282ouu0xg">
                    Pagado al repartidor
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-721" data-mp="26xg75gjeypd2">
                {rows.map(r => {
              const mMeta = methodMeta(r.payment_method);
              const isSaving = saving.has(r.id);
              return <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-id="element-722" data-mp="2zfu5rickqfd7">
                      <td className="px-6 py-2.5 text-gray-700 text-xs tabular-nums whitespace-nowrap font-medium" data-id="element-723" data-mp="h5poxnmx88im">
                        {r.order_number || r.id.slice(0, 8)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-900" data-id="element-724" data-mp="q5mf5toylffs">
                        {r.customer_name || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-725" data-mp="v6sa7y63desh">
                        {fmtTimeCR(r.created_at)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-726" data-mp="152d6dfohbkub">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${mMeta.chip}`} data-id="element-727" data-mp="15122lfv1ad37">
                          <mMeta.Icon className="w-3 h-3" data-id="element-728" data-mp="1dxtaqqmv1ie5" />
                          {mMeta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-700" data-id="element-729" data-mp="1nuxukjt8h1rs">
                        {r.delivery_zone || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700" data-id="element-730" data-mp="3juycqno0tw4a">
                        {r.delivery_subzone || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap" data-id="element-731" data-mp="3tn40fl78prdo">
                        {formatCurrency(r.delivery_fee)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap" data-id="element-732" data-mp="6hd1l71nukfc">
                        {r.delivery_paid_to_driver ? <span className="inline-flex flex-col gap-0.5" data-id="element-733" data-mp="2fzvcvunof7w">
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-bold text-xs" data-id="element-734" data-mp="ggkclnkxa2za">
                              <CheckCircle2 className="w-3.5 h-3.5" data-id="element-735" data-mp="biixteuj4ptm" />
                              Pagado
                            </span>
                            {r.delivery_paid_at && <span className="text-[10px] text-gray-500 tabular-nums" data-id="element-736" data-mp="2d9w0rycxsnn1">
                                {fmtFullCR(r.delivery_paid_at)}
                              </span>}
                            {isEmilius && r.delivery_driver_name && <span className="text-[10px] text-gray-600 font-medium" data-id="element-737" data-mp="izp7fwc2zh0r">
                                Repartidor: {r.delivery_driver_name}
                              </span>}
                          </span> : <span className="inline-flex items-center gap-1 text-amber-700 font-bold text-xs" data-id="element-738" data-mp="ezojnl33dth1">
                            <Clock className="w-3.5 h-3.5" data-id="element-739" data-mp="2o22aem7ewa" />
                            Pendiente
                          </span>}
                      </td>
                      <td className="px-3 py-2.5 text-center" data-id="element-740" data-mp="1ufduenpc4g1k">
                        <input type="checkbox" checked={r.delivery_paid_to_driver} disabled={isSaving} onChange={e => handleCheckboxChange(r, e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer disabled:opacity-50 disabled:cursor-wait" aria-label={`Marcar delivery del pedido ${r.order_number || r.id} como pagado al repartidor`} data-id="element-741" data-mp="1udsyatndgp1l" />
                      </td>
                    </tr>;
            })}
              </tbody>
              <tfoot data-id="element-742" data-mp="3qlu0fgonnoxy">
                <tr className="border-t-2 border-gray-200 bg-cyan-50/50" data-id="element-743" data-mp="3qnett2z8h1u8">
                  <td colSpan={6} className="px-6 py-3 font-black text-gray-900" data-id="element-744" data-mp="3lnsix06vnxol">
                    PENDIENTE POR PAGAR
                  </td>
                  <td className="px-3 py-3 text-right font-black text-amber-600 text-base tabular-nums" data-id="element-745" data-mp="1jve5oefa3hei">
                    {formatCurrency(totals.pendiente)}
                  </td>
                  <td colSpan={2} className="px-3 py-3 text-xs text-gray-500" data-id="element-746" data-mp="1we8cgdnb3ge6">
                    {rows.length} pedido{rows.length === 1 ? '' : 's'} con
                    delivery
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>}
      </CardContent>

      {/* Emilius — modal para registrar el nombre del repartidor al marcar
            un pedido como pagado. Solo se monta para Emilius (id 27). */}
      {isEmilius && driverModalRow && <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true" onClick={() => {
      setDriverModalRow(null);
      setDriverNameInput('');
    }} data-id="element-747" data-mp="1if08l7g77k3n">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-748" data-mp="2tw8clrmxmy7a">
            <div className="px-5 py-4 border-b border-gray-100" data-id="element-749" data-mp="d1eblqsyxtbe">
              <h3 className="text-base font-bold text-gray-900" data-id="element-750" data-mp="i0h308g1010w">
                Nombre del repartidor
              </h3>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-751" data-mp="mt0ixc26r88v">
                Pedido{' '}
                {driverModalRow.order_number || driverModalRow.id.slice(0, 8)} ·{' '}
                {formatCurrency(driverModalRow.delivery_fee)}
              </p>
            </div>
            <div className="px-5 py-4" data-id="element-752" data-mp="3v70qu62cfpfa">
              <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-753" data-mp="3v82eda0jq4dw">
                ¿Quién hizo la entrega?
              </label>
              <input type="text" autoFocus value={driverNameInput} onChange={e => setDriverNameInput(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') confirmDriverModal();
          }} placeholder="Ej: Juan Pérez" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent" data-id="element-754" data-mp="82tbdrdpv9my" />
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2" data-id="element-755" data-mp="1fui822g95bmf">
              <button onClick={() => {
            setDriverModalRow(null);
            setDriverNameInput('');
          }} className="h-10 px-4 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-756" data-mp="1av5jnd9qfwhq">
                Cancelar
              </button>
              <button onClick={confirmDriverModal} disabled={!driverNameInput.trim()} className="h-10 px-4 rounded-lg text-sm font-bold bg-cyan-600 hover:bg-cyan-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-757" data-mp="wwz4heckcl7e">
                Marcar pagado
              </button>
            </div>
          </div>
        </div>}
    </Card>;
}